import React, { useState, useEffect, useRef } from 'react'
import * as pdfjsLib from 'pdfjs-dist'

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`

interface PDFViewerProps {
  url: string
  title: string
  onClose?: () => void
  isFullscreen?: boolean
}

// Backend URL for proxying PDFs
const BACKEND_URL = (typeof window !== 'undefined' && window.location.hostname === 'localhost'
  ? 'http://localhost:3001'
  : 'https://8vosl9zwo9.execute-api.us-east-1.amazonaws.com')

function PDFViewer({ url, title, onClose, isFullscreen = false }: PDFViewerProps): JSX.Element {
  console.log('📄 PDFViewer component mounted with:', { url, title, isFullscreen })
  const [pdfDoc, setPdfDoc] = useState<any>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [scale, setScale] = useState(1.5)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [useFallback, setUseFallback] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  // Convert S3 URL to proxied URL
  const getProxiedUrl = (s3Url: string): string => {
    try {
      const urlObj = new URL(s3Url)
      // Extract the S3 key from the URL
      // Format: https://bucket.s3.region.amazonaws.com/key
      let s3Key = decodeURIComponent(urlObj.pathname.substring(1)) // Remove leading /
      
      // Base64 encode the key for the proxy endpoint
      const encodedKey = btoa(s3Key)
      return `${BACKEND_URL}/api/image/${encodedKey}`
    } catch (err) {
      console.error('Error converting URL to proxied URL:', err)
      return s3Url // Fallback to original URL
    }
  }

  // Load PDF document
  useEffect(() => {
    const loadPDF = async (): Promise<void> => {
      try {
        setIsLoading(true)
        setError(null)
        setUseFallback(false)
        setPdfDoc(null)
        setCurrentPage(1)
        setTotalPages(0)

        // Use proxied URL to avoid CORS issues
        const proxiedUrl = getProxiedUrl(url)
        console.log('Loading PDF from:', proxiedUrl)

        const loadingTask = pdfjsLib.getDocument({
          url: proxiedUrl,
          withCredentials: false
        })
        const pdf = await loadingTask.promise
        setPdfDoc(pdf)
        setTotalPages(pdf.numPages)
        setIsLoading(false)
      } catch (err) {
        console.error('Error loading PDF:', err)
        // Fallback to iframe if PDF.js fails so the document remains readable
        console.log('Falling back to iframe viewer')
        setUseFallback(true)
        setError(null)
        setIsLoading(false)
      }
    }
    loadPDF()
  }, [url])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault()
        goToPrevPage()
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault()
        goToNextPage()
      } else if (e.key === '+' || e.key === '=') {
        e.preventDefault()
        zoomIn()
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault()
        zoomOut()
      } else if (e.key === 'Escape' && isFullscreen && onClose) {
        e.preventDefault()
        onClose()
      } else if (e.key === 'Home') {
        e.preventDefault()
        setCurrentPage(1)
      } else if (e.key === 'End') {
        e.preventDefault()
        setCurrentPage(totalPages)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [currentPage, totalPages, scale, isFullscreen, onClose])

  // Render current page
  useEffect(() => {
    const renderPage = async (): Promise<void> => {
      if (!pdfDoc || !canvasRef.current) return
      
      try {
        const page = await pdfDoc.getPage(currentPage)
        const viewport = page.getViewport({ scale })
        const canvas = canvasRef.current
        const context = canvas.getContext('2d')
        
        if (!context) return
        
        canvas.height = viewport.height
        canvas.width = viewport.width
        
        const renderContext = {
          canvasContext: context,
          viewport: viewport
        }
        
        await page.render(renderContext).promise
      } catch (err) {
        console.error('Error rendering page:', err)
      }
    }
    
    renderPage()
  }, [pdfDoc, currentPage, scale])

  const goToNextPage = (): void => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const goToPrevPage = (): void => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const zoomIn = (): void => {
    setScale(prev => Math.min(prev + 0.25, 3))
  }

  const zoomOut = (): void => {
    setScale(prev => Math.max(prev - 0.25, 0.5))
  }

  const downloadPDF = (): void => {
    const link = document.createElement('a')
    link.href = getProxiedUrl(url)
    link.download = title || 'document.pdf'
    link.click()
  }

  const containerStyle: React.CSSProperties = isFullscreen ? {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100vh',
    backgroundColor: '#525659',
    zIndex: 3000,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  } : {
    width: '100%',
    height: '90vh',  // Use 90% of viewport height for better viewing
    minHeight: '600px',
    backgroundColor: '#525659',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  }

  return (
    <div style={containerStyle}>
      {/* Toolbar - only show if not using fallback */}
      {!useFallback && (
        <div style={{
          backgroundColor: '#323639',
          padding: '12px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          flexShrink: 0
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <h3 style={{ margin: 0, color: 'white', fontSize: '16px', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            📄 {title}
          </h3>
          <span style={{ color: '#aaa', fontSize: '14px' }}>
            Page {currentPage} of {totalPages}
          </span>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {/* Page Navigation */}
          <button
            onClick={goToPrevPage}
            disabled={currentPage <= 1}
            style={{
              padding: '6px 12px',
              backgroundColor: currentPage <= 1 ? '#444' : '#0078d4',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              transition: 'background-color 0.2s'
            }}
            title="Previous Page"
          >
            ← Prev
          </button>

          <button
            onClick={goToNextPage}
            disabled={currentPage >= totalPages}
            style={{
              padding: '6px 12px',
              backgroundColor: currentPage >= totalPages ? '#444' : '#0078d4',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              transition: 'background-color 0.2s'
            }}
            title="Next Page"
          >
            Next →
          </button>

          {/* Zoom Controls */}
          <div style={{ borderLeft: '1px solid #555', paddingLeft: '10px', marginLeft: '5px' }}>
            <button
              onClick={zoomOut}
              disabled={scale <= 0.5}
              style={{
                padding: '6px 10px',
                backgroundColor: scale <= 0.5 ? '#444' : '#0078d4',
                color: 'white',
                border: 'none',
                borderRadius: '4px 0 0 4px',
                cursor: scale <= 0.5 ? 'not-allowed' : 'pointer',
                fontSize: '16px'
              }}
              title="Zoom Out"
            >
              −
            </button>
            <span style={{
              padding: '6px 12px',
              backgroundColor: '#444',
              color: 'white',
              fontSize: '14px',
              display: 'inline-block'
            }}>
              {Math.round(scale * 100)}%
            </span>
            <button
              onClick={zoomIn}
              disabled={scale >= 3}
              style={{
                padding: '6px 10px',
                backgroundColor: scale >= 3 ? '#444' : '#0078d4',
                color: 'white',
                border: 'none',
                borderRadius: '0 4px 4px 0',
                cursor: scale >= 3 ? 'not-allowed' : 'pointer',
                fontSize: '16px'
              }}
              title="Zoom In"
            >
              +
            </button>
          </div>

          {/* Download Button */}
          <button
            onClick={downloadPDF}
            style={{
              padding: '6px 12px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              marginLeft: '5px'
            }}
            title="Download PDF"
          >
            ⬇ Download
          </button>

          {/* Close Button (only in fullscreen) */}
          {isFullscreen && onClose && (
            <button
              onClick={onClose}
              style={{
                padding: '6px 12px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                marginLeft: '5px'
              }}
              title="Close PDF Viewer (ESC)"
            >
              ✕ Close
            </button>
          )}
        </div>
      </div>
      )}

      {/* PDF Canvas Container */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        padding: '20px',
        backgroundColor: '#525659'
      }}>
        {isLoading && (
          <div style={{
            color: 'white',
            fontSize: '18px',
            marginTop: '50px'
          }}>
            Loading PDF...
          </div>
        )}
        
        {error && (
          <div style={{
            color: '#ff6b6b',
            fontSize: '16px',
            marginTop: '50px',
            textAlign: 'center'
          }}>
            <p>{error}</p>
            <p style={{ fontSize: '14px', marginTop: '10px' }}>
              Try downloading the PDF to view it locally.
            </p>
          </div>
        )}
        
        {useFallback && !isLoading && (() => {
          // Use direct S3 URL instead of proxy for better compatibility
          console.log('🎯 Rendering iframe fallback');
          console.log('🎯 Direct S3 URL:', url);
          return (
            <iframe
              src={url}
              title={title}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                backgroundColor: 'white'
              }}
              onLoad={() => console.log('✅ Iframe loaded successfully')}
              onError={() => console.error('❌ Iframe failed to load')}
            />
          );
        })()}
        
        {!isLoading && !error && !useFallback && (
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
              backgroundColor: 'white'
            }}
          />
        )}
      </div>

      {/* Keyboard shortcuts info */}
      {isFullscreen && (
        <div style={{
          position: 'absolute',
          bottom: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(0,0,0,0.7)',
          color: 'white',
          padding: '8px 15px',
          borderRadius: '20px',
          fontSize: '12px',
          opacity: 0.7
        }}>
          ← → Navigate pages • +/− Zoom • ESC Close
        </div>
      )}
    </div>
  )
}

export default PDFViewer
